// Detail pop-ups for Today's Activity: tap an order, an activity form or a call to see what is in it.
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import OrderShareButtons from "@/components/orders/OrderShareButtons";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
const PHOTO_TYPES = ["photo", "image", "camera", "signature", "file"];
const STATUS = { placed: "Placed", approved: "Approved", accepted: "Approved", fulfilled: "Fulfilled",
  partial_fulfilled: "Partly fulfilled", rejected: "Rejected", cancelled: "Cancelled", delivered: "Delivered" };

function when(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function Line({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-neutral-100 last:border-0 text-sm">
      <span className="text-neutral-500 shrink-0">{label}</span>
      <span className="text-neutral-900 text-right break-words min-w-0">{children}</span>
    </div>
  );
}

// ------------------------------------------------------------------ order
export function OrderDetailDialog({ order, onClose }) {
  const o = order;
  return (
    <Dialog open={!!o} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="ta-order-detail">
        {o && (
          <>
            <DialogHeader>
              <DialogTitle className="font-mono text-base">{o.order_no}</DialogTitle>
            </DialogHeader>
            <div>
              <Line label="Status">{STATUS[o.status] || o.status}</Line>
              <Line label="Booked at">{when(o.booking_ts)}</Line>
              <Line label="Outlet">{o.retailer_name || "—"}</Line>
            </div>
            <div className="space-y-2">
              {(o.lines || []).map((l) => (
                <div key={l.id} className="rounded-lg border border-neutral-200 p-2 text-sm">
                  <div className="font-semibold text-neutral-900">{l.display_name}</div>
                  <div className="flex justify-between text-xs text-neutral-600 mt-0.5">
                    <span>{l.qty_kg != null ? `${Number(l.qty_kg).toFixed(2)} kg` : `${l.qty_pcs ?? "—"} pcs`}
                      {l.unit_price ? ` × ${INR.format(l.unit_price)}` : ""}</span>
                    <span className="font-semibold">{INR.format(l.line_total ?? l.line_amount ?? 0)}</span>
                  </div>
                  {l.scheme_benefit_value && (
                    <div className="text-[11px] text-[#E5197F] font-semibold mt-1">🎁 Free: {l.scheme_benefit_value}</div>
                  )}
                  {(l.discount_amount || 0) > 0 && (
                    <div className="text-[11px] text-[#E5197F] mt-0.5">Scheme discount −{INR.format(l.discount_amount)}</div>
                  )}
                </div>
              ))}
            </div>
            <div>
              <Line label="Total kg">{(o.lines || []).reduce((a, l) => a + (Number(l.qty_kg) || 0), 0).toFixed(2)} kg</Line>
              {(o.scheme_discount_amount || 0) > 0 && <Line label="Scheme discount">−{INR.format(o.scheme_discount_amount)}</Line>}
              <Line label="Order value"><b>{INR.format(o.net_amount || 0)}</b></Line>
            </div>
            <OrderShareButtons order={o} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------- activity form
function answerOf(field, value) {
  const t = (field.field_type || "").toLowerCase();
  const optLabel = (c) => (field.options || []).find((x) => String(x.code) === String(c))?.label || String(c);
  if (value == null || value === "" || (Array.isArray(value) && !value.length)) return "—";
  if (Array.isArray(value)) return value.map(optLabel).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (["dropdown", "radio", "select", "single_select"].includes(t)) return optLabel(value);
  return String(value);
}

export function FormDetailDialog({ submissionId, title, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!submissionId) { setD(null); return undefined; }
    let off = false;
    setD(null);
    api.get(`/activity-submissions/${submissionId}`)
      .then((r) => { if (!off) setD(r.data); })
      .catch((e) => { if (!off) { toast.error(formatApiError(e)); onClose(); } });
    return () => { off = true; };
  }, [submissionId]); // eslint-disable-line react-hooks/exhaustive-deps
  const values = d?.values || {};
  return (
    <Dialog open={!!submissionId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="ta-form-detail">
        <DialogHeader><DialogTitle>{d?.form_name_snapshot || title || "Activity form"}</DialogTitle></DialogHeader>
        {!d ? (
          <div className="py-8 text-center"><Loader2 className="h-5 w-5 inline animate-spin text-neutral-400" /></div>
        ) : (
          <div className="space-y-2">
            <Line label="Filled at">{when(d.submission_ts)}</Line>
            {d.status && <Line label="Status">{d.status === "submitted" ? "Submitted" : d.status === "verified" ? "Verified" : d.status === "rejected" ? "Rejected" : d.status}</Line>}
            {(d.fields || []).map((f) => {
              const v = values[f.field_key];
              const isPhoto = PHOTO_TYPES.includes((f.field_type || "").toLowerCase());
              const refs = isPhoto ? (Array.isArray(v) ? v : v ? [v] : []) : [];
              return (
                <div key={f.id} className="py-1.5 border-b border-neutral-100 last:border-0">
                  <div className="text-xs text-neutral-500">{f.label}</div>
                  {isPhoto ? (
                    refs.length ? (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {refs.map((ref) => (
                          <AuthImage key={ref} refId={ref} w={400} alt={f.label}
                                     className="h-28 w-28 object-cover rounded-lg border border-neutral-200" />
                        ))}
                      </div>
                    ) : <div className="text-sm text-neutral-900">—</div>
                  ) : <div className="text-sm text-neutral-900 break-words">{answerOf(f, v)}</div>}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------- call
export function VisitDetailDialog({ visit, retailerName, reasonLabel, orderNos, onClose }) {
  const v = visit;
  const mins = v?.duration_sec != null ? Math.max(1, Math.round(v.duration_sec / 60)) : null;
  const noOrder = v && v.status !== "in_progress" && v.outcome === "non_productive";
  return (
    <Dialog open={!!v} onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="ta-visit-detail">
        {v && (
          <>
            <DialogHeader><DialogTitle>{noOrder ? "Call without order" : "Call details"}</DialogTitle></DialogHeader>
            <div>
              <Line label="Outlet">{retailerName || "—"}</Line>
              <Line label="Started">{when(v.check_in_ts)}</Line>
              <Line label="Ended">{v.status === "in_progress" ? "Still in progress" : when(v.check_out_ts)}</Line>
              {mins != null && <Line label="Time at outlet">{mins} min</Line>}
              {v.check_in_distance_m != null && <Line label="Distance from outlet">{Math.round(v.check_in_distance_m)} m</Line>}
              <Line label="Result">{v.status === "in_progress" ? "In progress" : noOrder ? "No order" : "Order booked"}</Line>
              {noOrder && <Line label="Reason">{reasonLabel || v.non_productive_reason || "—"}</Line>}
              {noOrder && (v.non_productive_remarks || v.no_order_note) && (
                <Line label="Remarks">{v.non_productive_remarks || v.no_order_note}</Line>
              )}
              {!noOrder && orderNos?.length > 0 && <Line label="Orders">{orderNos.join(", ")}</Line>}
              {v.notes && <Line label="Notes">{v.notes}</Line>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
